// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import type {RemoteAudioTrack, Room} from 'livekit-client';
import {Track} from 'livekit-client';
import {reaction} from 'mobx';

const logger = new Logger('VoiceOutputDeviceSync');
interface SinkableAudioContext extends AudioContext {
	setSinkId?: (
		sinkId:
			| string
			| {
					type: 'none';
			  },
	) => Promise<void>;
}
type RoomWithAudioContext = {
	audioContext?: SinkableAudioContext;
};
type SinkableMediaElement = HTMLMediaElement & {
	setSinkId?: (sinkId: string) => Promise<void>;
};
function normalizeSinkIdForBrowserApi(deviceId: string): string {
	return deviceId === 'default' ? '' : deviceId;
}
function isDeviceMissingError(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	if (error.name === 'NotFoundError') return true;
	return /not\s+found/i.test(error.message);
}
async function applyOutputDeviceToWebAudioMixer(room: Room, deviceId: string): Promise<void> {
	const audioContext = (room as unknown as RoomWithAudioContext).audioContext;
	if (!audioContext?.setSinkId) {
		return;
	}
	const sinkId = normalizeSinkIdForBrowserApi(deviceId);
	try {
		await audioContext.setSinkId(sinkId);
	} catch (error) {
		if (isDeviceMissingError(error)) {
			logger.warn('Web Audio mixer sink no longer available', {deviceId});
			return;
		}
		throw error;
	}
}
async function applyOutputDeviceToAttachedElements(room: Room, deviceId: string): Promise<void> {
	const sinkId = normalizeSinkIdForBrowserApi(deviceId);
	const operations: Array<Promise<void>> = [];
	room.remoteParticipants.forEach((participant) => {
		participant.audioTrackPublications.forEach((publication) => {
			if (publication.kind !== Track.Kind.Audio) return;
			const track = publication.track as RemoteAudioTrack | undefined;
			if (track?.kind === Track.Kind.Audio && typeof track.setSinkId === 'function') {
				operations.push(track.setSinkId(sinkId));
				return;
			}
			const attachedElements = (
				track as unknown as {
					attachedElements?: Array<SinkableMediaElement>;
				}
			)?.attachedElements;
			if (!attachedElements) return;
			for (const element of attachedElements) {
				if (typeof element.setSinkId === 'function') {
					operations.push(element.setSinkId(sinkId));
				}
			}
		});
	});
	const results = await Promise.allSettled(operations);
	for (const result of results) {
		if (result.status !== 'rejected') continue;
		if (isDeviceMissingError(result.reason)) {
			logger.warn('Audio element sink no longer available', {deviceId});
			continue;
		}
		logger.warn('Failed to apply audio output device to attached audio element', {deviceId, error: result.reason});
	}
}
async function applyOutputDeviceToRoom(room: Room, deviceId: string): Promise<void> {
	try {
		await room.switchActiveDevice('audiooutput', deviceId);
	} catch (error) {
		logger.warn('LiveKit failed to apply audio output device', {deviceId, error});
	}
	try {
		await applyOutputDeviceToWebAudioMixer(room, deviceId);
	} catch (error) {
		logger.warn('Failed to apply audio output device to Web Audio mixer', {deviceId, error});
	}
	try {
		await applyOutputDeviceToAttachedElements(room, deviceId);
	} catch (error) {
		logger.warn('Failed to apply audio output device to attached audio elements', {deviceId, error});
	}
}
export function bindOutputDeviceSync(room: Room): () => void {
	const initial = VoiceSettings.getOutputDeviceId();
	if (initial) {
		void applyOutputDeviceToRoom(room, initial);
	}
	const disposer = reaction(
		() => VoiceSettings.getOutputDeviceId(),
		(deviceId) => {
			if (!deviceId) return;
			void applyOutputDeviceToRoom(room, deviceId);
		},
		{
			fireImmediately: false,
		},
	);
	return disposer;
}
